import { AlertCircle } from '@untitledui/icons';
import { toast } from 'sonner';

import { Button } from '@/lib/base/buttons/button';
import { CloseButton } from '@/lib/base/buttons/close-button';
import { navigateToConversation } from '@/lib/navigation/appNavigate';
import { cx } from '@/utils/cx';

import {
  CHAT_TOAST_POSITION,
  CHAT_TOAST_WRAPPER_STYLE,
} from './chatRunToastStyles';

const FAILURE_TOAST_OPTIONS = {
  position: CHAT_TOAST_POSITION,
  style: CHAT_TOAST_WRAPPER_STYLE,
};

const REASON_MESSAGES: Record<'failed' | 'timeout', string> = {
  failed: "Your chat couldn't finish. Open it to check what happened.",
  timeout:
    'Your chat is taking longer than expected. Open it to check its status.',
};

/**
 * Show a persistent error toast when a main-chat run fails or times out
 * while the user is away from the conversation. Does not auto-dismiss — the
 * "View" action and the dismiss control are the only way to close it, since
 * it may be the sole way back to a conversation the user has navigated away
 * from.
 */
export function showChatRunFailureToast({
  orgId,
  conversationId,
  reason,
}: {
  orgId: string;
  conversationId: string;
  reason: 'failed' | 'timeout';
}): void {
  toast.custom(
    t => (
      <div
        role='alert'
        aria-live='assertive'
        aria-atomic='true'
        className={cx(
          'flex items-start gap-3 p-4 bg-error-50 border border-error-200 rounded-xl shadow-lg min-w-[320px] max-w-[420px]'
        )}
      >
        <div className='flex items-center justify-center size-9 rounded-full bg-error-100 shrink-0 mt-0.5'>
          <AlertCircle className='size-5 text-error-700' aria-hidden='true' />
        </div>

        <div className='flex-1 min-w-0 space-y-2'>
          <div className='min-w-0'>
            <p className='text-sm font-semibold text-error-700'>
              {REASON_MESSAGES[reason]}
            </p>
          </div>

          <Button
            size='sm'
            color='secondary'
            onClick={() => {
              // In-app SPA navigation — a hard reload here would discard
              // client state and violate the epic's no-reload success
              // metric (asserted by navigation-resume.spec.ts).
              navigateToConversation(orgId, conversationId);
              toast.dismiss(t);
            }}
          >
            View
          </Button>
        </div>

        <CloseButton
          size='xs'
          label='Dismiss'
          onPress={() => toast.dismiss(t)}
          className='shrink-0'
        />
      </div>
    ),
    { ...FAILURE_TOAST_OPTIONS, duration: Infinity }
  );
}
